/**
 * PDP + mini-cart loyalty points earn callouts.
 * Reads `window.Theme.loyaltyEarn` from {@link snippets/scripts.liquid}.
 * Listens for `variant:changed` (PDP), `section:updated` (style-selector / section morph follow-up).
 * Bundle builder: strip has `data-loyalty-earn-bundle`; qualifying cents prefer `product-add-to-cart[data-bundle-display-price-cents]` (written by `updateBundleState` in `product-add-to-cart.js`, same as the button total), then sum `data-items` + discount. `variant:changed` fires after `data-items` is updated so the strip stays in sync.
 *
 * @typedef {object} LoyaltyEarnThemeConfig
 * @property {boolean} enabled - True if any earn placement is on (for script load / coarse checks)
 * @property {boolean} enabledTitlePrice - PDP badge next to price (`loyalty_points_earn_enabled_title_price`)
 * @property {boolean} enabledAddToCart - PDP strip below ATC (`loyalty_points_earn_enabled_add_to_cart`)
 * @property {boolean} enabledCart - Cart drawer callout (`loyalty_points_earn_enabled_cart`)
 * @property {number} pointsPerDollar
 * @property {string} learnMoreUrl
 * @property {boolean} isLoggedIn
 */

import { ThemeEvents } from "@theme/events";

/** @param {string | number | undefined} sectionId */
function shopifySectionSelector(sectionId) {
  if (sectionId == null || sectionId === "") return null;
  const id = String(sectionId).replace(/^shopify-section-/, "");
  return `#shopify-section-${id}`;
}

/** @returns {LoyaltyEarnThemeConfig | null} */
function readThemeConfig() {
  const raw = /** @type {unknown} */ (window).Theme?.loyaltyEarn;
  if (!raw || typeof raw !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const legacyAny = Boolean(o.enabled);
  const hasTitle = "enabledTitlePrice" in o;
  const hasAtc = "enabledAddToCart" in o;
  const hasCart = "enabledCart" in o;
  const enabledTitlePrice = hasTitle ? Boolean(o.enabledTitlePrice) : legacyAny;
  const enabledAddToCart = hasAtc ? Boolean(o.enabledAddToCart) : legacyAny;
  const enabledCart = hasCart ? Boolean(o.enabledCart) : legacyAny;
  return {
    enabled: enabledTitlePrice || enabledAddToCart || enabledCart,
    enabledTitlePrice,
    enabledAddToCart,
    enabledCart,
    pointsPerDollar: Math.max(1, Math.min(20, Number(o.pointsPerDollar) || 1)),
    learnMoreUrl: typeof o.learnMoreUrl === "string" ? o.learnMoreUrl : "",
    isLoggedIn: Boolean(o.isLoggedIn),
  };
}

/**
 * Whole currency units from cents (floored to whole dollars, e.g. 16099 → 160).
 * @param {number} cents
 * @returns {number}
 */
export function qualifyingWholeUnitsFromCents(cents) {
  if (!Number.isFinite(cents) || cents <= 0) return 0;
  return Math.floor(cents / 100);
}

/**
 * @param {number} cents
 * @param {number} pointsPerDollar
 * @returns {number}
 */
export function computeEarnPoints(cents, pointsPerDollar) {
  const units = qualifyingWholeUnitsFromCents(cents);
  const rate = Number.isFinite(pointsPerDollar) && pointsPerDollar > 0 ? pointsPerDollar : 1;
  return Math.ceil(units * rate);
}

/**
 * @param {Record<string, unknown>} item - cart.js line
 * @returns {boolean}
 */
export function isShippingProtectionLine(item) {
  const title = String(item.title ?? "").toLowerCase();
  const vendor = String(item.vendor ?? "").toLowerCase();
  const type = String(item.product_type ?? "").toLowerCase();
  if (vendor.includes("corso")) return true;
  if (title.includes("shipping protection") || title.includes("package protection")) return true;
  if (type.includes("protection") && (type.includes("shipping") || type.includes("package"))) return true;
  return false;
}

/**
 * Sum `final_line_price` for cart lines excluding shipping protection add-ons.
 * @param {Record<string, unknown>} cart
 * @returns {number}
 */
export function cartQualifyingCents(cart) {
  const items = cart?.items;
  if (!Array.isArray(items)) return 0;
  let sum = 0;
  for (const item of items) {
    if (!item || typeof item !== "object") continue;
    if (isShippingProtectionLine(/** @type {Record<string, unknown>} */ (item))) continue;
    const line = Number(/** @type {Record<string, unknown>} */ (item).final_line_price);
    if (Number.isFinite(line)) sum += line;
  }
  return sum;
}

// ─────────────────────────────────────────────────────────────────────────────
// Collection multiplier is now sourced from theme settings and stamped onto line
// items as properties._loyalty_multiplier at add-to-cart time by loyalty-atc-interceptor.js.
// The old collection-ID localStorage cache has been removed.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {HTMLElement} host - `product-add-to-cart` bundle host
 * @returns {number} 0–100
 */
function resolveBundleDiscountPercent(host) {
  const raw = host.dataset.bundleDiscountPercent;
  const fromHost = Number.parseInt(String(raw ?? ""), 10);
  if (Number.isFinite(fromHost) && fromHost > 0) return Math.min(100, fromHost);
  const atc = document.querySelector("bundle-atc-component");
  const fromAtc = atc instanceof HTMLElement ? Number.parseInt(String(atc.dataset.discountPercentage || ""), 10) : NaN;
  if (Number.isFinite(fromAtc) && fromAtc > 0) return Math.min(100, fromAtc);
  return 0;
}

/**
 * Bundle qualifying cents: prefer `data-bundle-display-price-cents` on the ATC host (button total),
 * else sum `data-items` + discount (same math as fallback).
 * @param {HTMLElement} stripRoot - PDP earn root inside `product-add-to-cart`
 * @returns {number | null} Discounted total cents, or null if not inside a bundle host
 */
export function bundleBuilderQualifyingCents(stripRoot) {
  const host = stripRoot.closest("product-add-to-cart[data-items]");
  if (!(host instanceof HTMLElement)) return null;
  // Same cents as the ATC button (`product-add-to-cart.js` `updateBundleState`), set after `data-items` updates.
  const published = Number(host.dataset.bundleDisplayPriceCents);
  if (Number.isFinite(published) && published >= 0) return published;

  let items = [];
  try {
    items = JSON.parse(host.dataset.items || "[]");
  } catch {
    return 0;
  }
  if (!Array.isArray(items)) return 0;
  let sum = 0;
  for (const item of items) {
    if (!item || typeof item !== "object") continue;
    const row = /** @type {Record<string, unknown>} */ (item);
    if (row.id == null || row.id === "") continue;
    const price = Number(row.price);
    if (Number.isFinite(price)) sum += price;
  }
  const pct = resolveBundleDiscountPercent(host);
  if (pct <= 0) return sum;
  const mult = (100 - pct) / 100;
  return Math.floor(sum * mult);
}

/**
 * @param {Element | null | undefined} section
 * @param {string | undefined} productId - from earn root `data-product-id`
 */
function queryVariantPickerForProduct(section, productId) {
  if (!section) return null;
  if (productId) {
    const escaped = typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(productId) : productId;
    const byAttr = section.querySelector(`product-option-selectors[data-product-id="${escaped}"]`);
    if (byAttr) return byAttr;
    for (const el of section.querySelectorAll("product-option-selectors")) {
      if (el instanceof HTMLElement && String(el.dataset.productId) === String(productId)) return el;
    }
  }
  return section.querySelector("product-option-selectors");
}

/**
 * Visible PDP price row (`[data-product-price-wrapper]`) — same cents as `assets/product-title-price.js` (displayed price).
 * @param {Element | null | undefined} section
 * @param {string | undefined} productId
 * @returns {HTMLElement | null}
 */
function findProductPriceWrapperForProduct(section, productId) {
  if (!section) return null;
  if (productId) {
    const escaped = typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(productId) : productId;
    const byAttr = section.querySelector(`[data-product-price-wrapper][data-product-id="${escaped}"]`);
    if (byAttr instanceof HTMLElement) return byAttr;
    for (const el of section.querySelectorAll("[data-product-price-wrapper]")) {
      if (el instanceof HTMLElement && String(el.dataset.productId) === String(productId)) return el;
    }
  }
  const first = section.querySelector("[data-product-price-wrapper]");
  return first instanceof HTMLElement ? first : null;
}

/**
 * @param {HTMLElement | null} wrapper
 * @returns {{ price: number; compare_at_price: number | null } | null}
 */
function readDisplayPriceAsVariantLike(wrapper) {
  if (!(wrapper instanceof HTMLElement)) return null;
  const raw = wrapper.dataset.variantPriceCents;
  if (raw === undefined || raw === "") return null;
  const price = Number(raw);
  if (!Number.isFinite(price)) return null;
  const cRaw = wrapper.dataset.compareAtPriceCents;
  const compare = cRaw !== undefined && cRaw !== "" ? Number(cRaw) : NaN;
  return {
    price,
    compare_at_price: Number.isFinite(compare) ? compare : null,
  };
}

/**
 * Prefer machine-readable cents on the visible price wrapper (style morph + title-price stay in sync).
 * @param {Element | null | undefined} section
 * @param {string | undefined} productId
 * @param {unknown} eventVariant
 */
function resolvePdpEarnVariant(section, productId, eventVariant) {
  const fromDisplay = readDisplayPriceAsVariantLike(findProductPriceWrapperForProduct(section, productId));
  if (fromDisplay && Number.isFinite(fromDisplay.price)) return fromDisplay;
  if (!eventVariant || typeof eventVariant !== "object") return null;
  const ev = /** @type {Record<string, unknown>} */ (eventVariant);
  const evPrice = Number(ev.price);
  if (!Number.isFinite(evPrice)) return null;
  const c = Number(ev.compare_at_price);
  return {
    price: evPrice,
    compare_at_price: Number.isFinite(c) ? c : null,
  };
}

/**
 * @param {HTMLElement} root
 * @param {LoyaltyEarnThemeConfig | null} cfg
 * @returns {boolean}
 */
function placementEnabledForRoot(root, cfg) {
  if (!cfg) return false;
  const layout = root.dataset.loyaltyLayout || "";
  if (layout === "badge") return Boolean(cfg.enabledTitlePrice);
  if (layout === "strip") return Boolean(cfg.enabledAddToCart);
  if (layout === "cart") return Boolean(cfg.enabledCart);
  return Boolean(cfg.enabled);
}

/**
 * @param {HTMLElement} root
 * @param {LoyaltyEarnThemeConfig | null} cfg
 */
function updateRoot(root, cfg) {
  if (!placementEnabledForRoot(root, cfg)) {
    root.hidden = true;
    root.removeAttribute("data-loyalty-calculating");
    return;
  }

  const layout = root.dataset.loyaltyLayout || "";
  const valueEls = root.querySelectorAll("[data-loyalty-earn-value]");
  const guestActions = root.querySelector("[data-loyalty-earn-guest-actions]");
  const learnLink = root.querySelector("[data-loyalty-earn-learn-more]");

  if (learnLink instanceof HTMLAnchorElement && cfg.learnMoreUrl) {
    learnLink.href = cfg.learnMoreUrl;
  }

  if (cfg.isLoggedIn) {
    guestActions?.setAttribute("hidden", "");
  } else {
    guestActions?.removeAttribute("hidden");
  }

  let cents = 0;
  if (root.dataset.loyaltyContext === "pdp") {
    if (root.hasAttribute("data-loyalty-earn-bundle")) {
      const bundleCents = bundleBuilderQualifyingCents(root);
      cents = bundleCents != null ? bundleCents : Number(root.dataset.variantPriceCents);
    } else {
      cents = Number(root.dataset.variantPriceCents);
    }
  } else if (root.dataset.loyaltyContext === "cart") {
    cents = Number(root.dataset.cartQualifyingCents);
  }

  if (!Number.isFinite(cents) || cents < 0) cents = 0;

  const hideBadgeForSale = root.dataset.loyaltyHideBadgeSale === "true";
  const hideBadgeForPromo = root.dataset.loyaltyHideBadgePromo === "true";

  if (layout === "badge") {
    const hide = hideBadgeForSale || hideBadgeForPromo;
    root.classList.toggle("hidden", hide);
    if (hide) {
      root.removeAttribute("data-loyalty-calculating");
      return;
    }
  }

  root.hidden = false;

  const loyaltyContext = root.dataset.loyaltyContext || "";

  let cartItems = [];
  if (loyaltyContext === "cart") {
    try {
      cartItems = JSON.parse(root.dataset.cartItemsJson || "[]");
    } catch {
      cartItems = [];
    }
  }

  const engine = window.BWW?.loyaltyEngine;
  let pts;
  let activeMult = 1;

  if (engine) {
    if (loyaltyContext === "pdp") {
      // Multiplier is stamped by Liquid onto data-loyalty-multiplier (Liquid has full collection access,
      // no 50-product limit). Use it directly — no separate JS product map lookup needed.
      activeMult = Number(root.dataset.loyaltyMultiplier) > 1 ? Number(root.dataset.loyaltyMultiplier) : 1;
      pts = engine.calculatePoints(cents, activeMult);
    } else if (loyaltyContext === "cart") {
      if (cartItems.length > 0) {
        // Primary: read _loyalty_multiplier line item property stamped by loyalty-atc-interceptor.js.
        // Fallback: check loyaltyVariantMultiplierMap for items added before the interceptor was active.
        const vMap = /** @type {Record<string, number>} */ (window.BWW?.loyaltyVariantMultiplierMap ?? {});
        const itemsForCalc = cartItems
          .filter(item => !isShippingProtectionLine(/** @type {Record<string, unknown>} */ (item)))
          .map(item => {
            const props = /** @type {any} */ (item).properties;
            let mult = props && Number(props._loyalty_multiplier) > 1 ? Number(props._loyalty_multiplier) : 0;
            if (mult < 1) {
              const vid = String(/** @type {any} */ (item).variant_id || "");
              mult = Number(vMap[vid]) > 1 ? Number(vMap[vid]) : 1;
            }
            return {
              cents: Number(/** @type {any} */ (item).final_line_price),
              multiplier: mult,
            };
          });
        pts = engine.calculateCartPoints(itemsForCalc);
        for (const item of itemsForCalc) {
          if (item.multiplier > activeMult) activeMult = item.multiplier;
        }
      } else {
        pts = engine.calculatePoints(cents, 1);
      }
    } else {
      pts = engine.calculatePoints(cents, 1);
    }
  } else {
    pts = computeEarnPoints(cents, cfg.pointsPerDollar);
  }

  valueEls.forEach(el => {
    el.textContent = String(pts);
  });

  // const campaignBadge = root.querySelector("[data-loyalty-campaign-badge]");
  // if (campaignBadge instanceof HTMLElement) {
  //   if (activeMult > 1) {
  //     const multDisplay = Number.isInteger(activeMult) ? activeMult : activeMult.toFixed(1);
  //     campaignBadge.textContent = "⚡ " + multDisplay + "\xD7 Points";
  //     campaignBadge.hidden = false;
  //   } else {
  //     campaignBadge.hidden = true;
  //   }
  // }

  root.removeAttribute("data-loyalty-calculating");
}

/**
 * @param {LoyaltyEarnThemeConfig | null} cfg
 */
function refreshAll(cfg) {
  document.querySelectorAll("[data-loyalty-earn-root]").forEach(el => {
    if (el instanceof HTMLElement) updateRoot(el, cfg);
  });
}

/**
 * @param {HTMLElement} pdpRoot
 * @param {unknown} variant
 */
function syncPdpDatasetFromVariant(pdpRoot, variant) {
  if (!variant || typeof variant !== "object") return;
  const v = /** @type {Record<string, unknown>} */ (variant);
  const price = Number(v.price);
  const compare = Number(v.compare_at_price);
  pdpRoot.dataset.variantPriceCents = Number.isFinite(price) ? String(Math.round(price)) : "0";
  const onSale = Number.isFinite(compare) && Number.isFinite(price) && compare > price;
  pdpRoot.dataset.loyaltyHideBadgeSale = onSale ? "true" : "false";
}

/**
 * @returns {Promise<void>}
 */
async function refreshCartDataset() {
  const cartRoots = document.querySelectorAll('[data-loyalty-earn-root][data-loyalty-context="cart"]');
  if (cartRoots.length === 0) return;

  const rootPath = window.Shopify?.routes?.root || "/";
  try {
    const res = await fetch(`${rootPath}cart.js`);
    if (!res.ok) return;
    const cart = await res.json();
    const cents = cartQualifyingCents(cart);
    const itemsJson = JSON.stringify(Array.isArray(cart.items) ? cart.items : []);
    cartRoots.forEach(el => {
      if (el instanceof HTMLElement) {
        el.dataset.cartQualifyingCents = String(cents);
        el.dataset.cartItemsJson = itemsJson;
      }
    });
  } catch {
    /* ignore */
  }
}

function onVariantChanged(/** @type {Event} */ event) {
  const e = /** @type {CustomEvent} */ (event);
  const variant = e.detail?.variant;
  const sectionId = /** @type {string | undefined} */ (e.detail?.sectionId);
  const cfg = readThemeConfig();

  const sectionSel = shopifySectionSelector(sectionId);
  const roots = sectionSel
    ? document.querySelectorAll(`${sectionSel} [data-loyalty-earn-root][data-loyalty-context="pdp"]`)
    : document.querySelectorAll('[data-loyalty-earn-root][data-loyalty-context="pdp"]');

  roots.forEach(el => {
    if (!(el instanceof HTMLElement)) return;

    if (el.hasAttribute("data-loyalty-earn-bundle")) {
      const bundleCents = bundleBuilderQualifyingCents(el);
      if (bundleCents != null) el.dataset.variantPriceCents = String(bundleCents);
      updateRoot(el, cfg);
      return;
    }

    if (!variant) return;

    const rootProductId = el.dataset.productId;
    if (rootProductId && variant.product_id != null && String(variant.product_id) !== String(rootProductId)) {
      // Stale product ID: happens when product-add-to-cart uses data-skip-subtree-update so
      // its children (including this strip) survive a style-selector morph unchanged.
      // The ATC element's own data-product-id IS updated by morph because copyAttributes
      // applies to the element itself, not its children. Use it as a fallback match.
      const atcParent = el.closest("product-add-to-cart");
      const atcProductId = atcParent instanceof HTMLElement ? atcParent.dataset.productId : null;
      if (!atcProductId || String(atcProductId) !== String(variant.product_id)) return;
      // Heal the stale ID so future events match without this fallback.
      el.dataset.productId = String(variant.product_id);
    }

    const section = el.closest(".shopify-section");
    const resolved = resolvePdpEarnVariant(section, el.dataset.productId, variant);
    if (!resolved) return;
    syncPdpDatasetFromVariant(el, resolved);
    updateRoot(el, cfg);
  });
}

/**
 * PDP earn callouts are Liquid-rendered with `selected_or_first_available_variant`, but the picker may
 * resolve a different variant on init (saved options, gift-card grid, etc.). `variant:changed` can also
 * fire before this module loads. Sync prefers `[data-product-price-wrapper]` machine-readable cents
 * (updated by `product-title-price.js`, SSR on morph) per `data-product-id`, then falls back to the
 * variant form + embedded variants JSON.
 */
function syncPdpLoyaltyFromVariantPicker() {
  const cfg = readThemeConfig();
  document.querySelectorAll('[data-loyalty-earn-root][data-loyalty-context="pdp"]').forEach(root => {
    if (!(root instanceof HTMLElement)) return;
    if (root.hasAttribute("data-loyalty-earn-bundle")) {
      const bundleCents = bundleBuilderQualifyingCents(root);
      if (bundleCents != null) root.dataset.variantPriceCents = String(bundleCents);
      updateRoot(root, cfg);
      return;
    }
    const section = root.closest(".shopify-section");
    const wrapper = findProductPriceWrapperForProduct(section, root.dataset.productId);
    const displayVariant = readDisplayPriceAsVariantLike(wrapper);
    if (displayVariant) {
      // Heal stale data-product-id on the strip after a style-selector morph.
      // product-add-to-cart uses data-skip-subtree-update so its children (including
      // the loyalty strip) are not replaced during morph. The price wrapper is updated
      // to the new product, but the strip's data-product-id stays as the old product.
      // Without this heal, onVariantChanged silently skips the strip because the IDs
      // no longer match the new product's variant:changed events.
      if (
        wrapper instanceof HTMLElement &&
        wrapper.dataset.productId &&
        wrapper.dataset.productId !== root.dataset.productId
      ) {
        root.dataset.productId = wrapper.dataset.productId;
      }
      syncPdpDatasetFromVariant(root, displayVariant);
      updateRoot(root, cfg);
      return;
    }

    const selector = queryVariantPickerForProduct(section, root.dataset.productId);
    if (!selector) return;
    const form = selector.querySelector("form[data-variant-picker-form]");
    if (!form) return;
    const input = form.querySelector("input[data-variant-id-input]");
    const variantsScript = form.querySelector("[data-all-variants-data]");
    if (!(input instanceof HTMLInputElement) || !variantsScript?.textContent) return;
    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch {
      return;
    }
    if (!Array.isArray(variants)) return;
    const vid = Number(input.value);
    const variant = variants.find(v => Number(v.id) === vid);
    if (!variant || typeof variant.price !== "number") return;
    syncPdpDatasetFromVariant(root, variant);
    updateRoot(root, cfg);
  });
}

/**
 * Clicks the header's own sign-in control. The theme renders two, one per breakpoint — the
 * account drawer below 750px and the account popover above it — and hides the other with CSS,
 * so the visible one is the one to drive.
 * @returns {boolean} Whether a control was found and clicked.
 */
function openSignIn() {
  const triggers = [".account-drawer .account-button", ".account-popover .account-button"];
  for (const selector of triggers) {
    const el = document.querySelector(selector);
    if (!(el instanceof HTMLElement)) continue;
    const visible = typeof el.checkVisibility === "function" ? el.checkVisibility() : el.offsetParent !== null;
    if (!visible) continue;
    el.click();
    return true;
  }
  return false;
}

function initSignInDelegation() {
  document.addEventListener(
    "click",
    event => {
      const t = event.target;
      if (!(t instanceof Element)) return;
      const btn = t.closest("[data-loyalty-open-sign-in]");
      if (!(btn instanceof HTMLElement)) return;
      event.preventDefault();
      if (openSignIn()) return;

      // Customer accounts are off, or the header is not on this page: fall back to Rivo's drawer.
      window.location.hash = "#rivo";
      const rivo = document.querySelector('a[href="#rivo"]');
      if (rivo instanceof HTMLElement) rivo.click();
    },
    false
  );
}

function bind() {
  initSignInDelegation();

  const cfg = readThemeConfig();
  refreshAll(cfg);

  document.addEventListener("variant:changed", onVariantChanged);

  document.addEventListener("section:updated", () => {
    queueMicrotask(() => {
      syncPdpLoyaltyFromVariantPicker();
      refreshAll(readThemeConfig());
    });
  });

  document.addEventListener(ThemeEvents.cartUpdate, () => {
    void refreshCartDataset().then(() => refreshAll(readThemeConfig()));
  });

  document.addEventListener(ThemeEvents.sectionMorph, () => {
    queueMicrotask(async () => {
      await refreshCartDataset();
      syncPdpLoyaltyFromVariantPicker();
      refreshAll(readThemeConfig());
    });
  });

  document.addEventListener("rivo-js-loaded", () => {
    refreshAll(readThemeConfig());
  });

  document.addEventListener("loyaltyEngine:ready", () => {
    refreshAll(readThemeConfig());
  });

  // When the subscription toggle switches between one-time and subscribe, update all PDP
  // loyalty roots for that product to reflect the effective checkout price. The event is
  // dispatched on product-add-to-cart (bubbles), so event.target carries data-product-id
  // and data-price (the regular variant price as a fallback).
  document.addEventListener("subscription:changed", event => {
    const detail = /** @type {any} */ (event).detail;
    if (!detail) return;
    const atcEl = event.target instanceof HTMLElement ? event.target : null;
    const productId = atcEl?.dataset?.productId;
    if (!productId) return;

    const effectiveCents =
      detail.option === "subscribe" && detail.subscriptionPrice != null
        ? Number(detail.subscriptionPrice)
        : Number(atcEl?.dataset?.price ?? 0);

    const freshCfg = readThemeConfig();
    document
      .querySelectorAll(
        `[data-loyalty-earn-root][data-loyalty-context="pdp"][data-product-id="${CSS.escape(productId)}"]`
      )
      .forEach(el => {
        if (!(el instanceof HTMLElement)) return;
        el.dataset.variantPriceCents = String(effectiveCents);
        updateRoot(el, freshCfg);
      });
  });

  void refreshCartDataset().then(() => refreshAll(readThemeConfig()));

  window.setTimeout(() => {
    syncPdpLoyaltyFromVariantPicker();
    refreshAll(readThemeConfig());
  }, 0);
}

if (document.documentElement.dataset.bwwLoyaltyEarnBound !== "1") {
  document.documentElement.dataset.bwwLoyaltyEarnBound = "1";
  bind();
}
